import { create } from "zustand";
import axios from "axios";
import { http } from "../api/http.js";
import { acceptInvitation as acceptInvitationApi } from "../api/invitations.js";

export interface SafeUser {
  id: string;
  orgId: string;
  email: string;
  name: string;
  role: string;
  v1Role?: string | null;
  createdAt: string;
  lastLoginAt: string | null;
}

interface Org {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
}

interface AuthResponse {
  success: boolean;
  token: string;
  user: SafeUser;
  org: Org;
  app: string;
}

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: SafeUser | null;
  org: Org | null;
  app: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  registerCreator: (
    email: string,
    password: string,
    name: string,
    creatorName?: string,
  ) => Promise<void>;
  acceptInvitation: (
    rawToken: string,
    payload: { password: string; name?: string },
  ) => Promise<void>;
  logout: () => Promise<void>;
  deleteAccount: (confirmation: string) => Promise<void>;
  clearError: () => void;
}

const STORAGE_KEYS = {
  token: "content_uploader_token",
  refreshToken: "content_uploader_refresh_token",
  user: "content_uploader_user",
  org: "content_uploader_org",
  app: "content_uploader_app",
} as const;

function loadInitialState(): {
  token: string | null;
  refreshToken: string | null;
  user: SafeUser | null;
  org: Org | null;
  app: string | null;
  isAuthenticated: boolean;
} {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.token);
    const refreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken);
    if (token === null)
      return {
        token: null,
        refreshToken: null,
        user: null,
        org: null,
        app: null,
        isAuthenticated: false,
      };
    const userStr = localStorage.getItem(STORAGE_KEYS.user);
    const orgStr = localStorage.getItem(STORAGE_KEYS.org);
    if (userStr === null || orgStr === null) {
      localStorage.removeItem(STORAGE_KEYS.token);
      localStorage.removeItem(STORAGE_KEYS.refreshToken);
      return {
        token: null,
        refreshToken: null,
        user: null,
        org: null,
        app: null,
        isAuthenticated: false,
      };
    }
    const user = JSON.parse(userStr) as SafeUser;
    const org = JSON.parse(orgStr) as Org;
    const app = localStorage.getItem(STORAGE_KEYS.app);
    return { token, refreshToken, user, org, app, isAuthenticated: true };
  } catch {
    localStorage.removeItem(STORAGE_KEYS.token);
    localStorage.removeItem(STORAGE_KEYS.refreshToken);
    localStorage.removeItem(STORAGE_KEYS.user);
    localStorage.removeItem(STORAGE_KEYS.org);
    localStorage.removeItem(STORAGE_KEYS.app);
    return {
      token: null,
      refreshToken: null,
      user: null,
      org: null,
      app: null,
      isAuthenticated: false,
    };
  }
}

function persistAuth(data: AuthResponse & { refreshToken?: string }): void {
  localStorage.setItem(STORAGE_KEYS.token, data.token);
  if (data.refreshToken !== undefined) {
    localStorage.setItem(STORAGE_KEYS.refreshToken, data.refreshToken);
  }
  localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(data.user));
  localStorage.setItem(STORAGE_KEYS.org, JSON.stringify(data.org));
  localStorage.setItem(STORAGE_KEYS.app, data.app);
}

function authError(err: unknown, fallback: string): string {
  if (!axios.isAxiosError(err)) return fallback;
  const body = err.response?.data as
    | { error?: { message?: string }; message?: string }
    | undefined;
  return body?.error?.message ?? body?.message ?? err.message;
}

export const useAuthStore = create<AuthState>((set) => ({
  ...loadInitialState(),
  isLoading: false,
  error: null,

  deleteAccount: async (confirmation) => {
    set({ isLoading: true, error: null });
    try {
      await http.delete("/v1/identity/me", { data: { confirmation } });
      localStorage.removeItem(STORAGE_KEYS.token);
      localStorage.removeItem(STORAGE_KEYS.refreshToken);
      localStorage.removeItem(STORAGE_KEYS.user);
      localStorage.removeItem(STORAGE_KEYS.org);
      localStorage.removeItem(STORAGE_KEYS.app);
      set({
        token: null,
        refreshToken: null,
        user: null,
        org: null,
        app: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    } catch (err: unknown) {
      set({
        error: authError(err, "Could not delete account"),
        isLoading: false,
      });
      throw err;
    }
  },

  login: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await http.post<
        AuthResponse & { refreshToken?: string }
      >("/v1/identity/login", {
        email,
        password,
      });
      persistAuth(data);
      set({
        token: data.token,
        refreshToken: data.refreshToken ?? null,
        user: data.user,
        org: data.org,
        app: data.app,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (err: unknown) {
      set({ error: authError(err, "Login failed"), isLoading: false });
      throw err;
    }
  },

  registerCreator: async (email, password, name, creatorName) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await http.post<
        AuthResponse & { refreshToken?: string }
      >("/auth/register/creator", {
        email,
        password,
        name,
        creatorName,
      });
      persistAuth(data);
      set({
        token: data.token,
        refreshToken: data.refreshToken ?? null,
        user: data.user,
        org: data.org,
        app: data.app,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (err: unknown) {
      set({ error: authError(err, "Sign up failed"), isLoading: false });
      throw err;
    }
  },

  acceptInvitation: async (rawToken, payload) => {
    set({ isLoading: true, error: null });
    try {
      const data = await acceptInvitationApi(rawToken, payload);
      const normalized: AuthResponse & { refreshToken?: string } = {
        success: true,
        token: data.token,
        user: {
          id: data.user.id,
          orgId: data.user.orgId,
          email: data.user.email,
          name: data.user.name,
          role: data.user.role,
          v1Role: data.user.v1Role,
          createdAt: data.user.createdAt ?? "",
          lastLoginAt: data.user.lastLoginAt ?? null,
        },
        org: data.org,
        app: data.app,
      };
      persistAuth(normalized);
      set({
        token: normalized.token,
        refreshToken: normalized.refreshToken ?? null,
        user: normalized.user,
        org: normalized.org,
        app: normalized.app,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error && err.message !== ""
          ? err.message
          : authError(err, "Could not accept invitation");
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  logout: async () => {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken);
    // Fire-and-forget server revocation: never block local sign-out on a slow
    // or unreachable backend. The access token (JWT) and refresh token are both
    // removed below, so the client is fully signed out regardless.
    if (refreshToken !== null) {
      http.post("/v1/identity/logout", { refreshToken }).catch(() => {
        // Best-effort revocation; local sign-out continues either way.
      });
    }
    localStorage.removeItem(STORAGE_KEYS.token);
    localStorage.removeItem(STORAGE_KEYS.refreshToken);
    localStorage.removeItem(STORAGE_KEYS.user);
    localStorage.removeItem(STORAGE_KEYS.org);
    localStorage.removeItem(STORAGE_KEYS.app);
    set({
      token: null,
      refreshToken: null,
      user: null,
      org: null,
      app: null,
      isAuthenticated: false,
      error: null,
    });
  },

  clearError: () => set({ error: null }),
}));
